import {
  createContext,
  FC,
  MutableRefObject,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { v4 as uuid } from 'uuid';

import { RESERVED_JAVASCRIPT_KEYWORDS } from 'dashboard/lib/utils/customCode';
import { captureException } from 'lib/reporting';

/**
 * These timeouts are arbitrary, but are generally an order of magnitude longer than the expected
 * load times. If you see a timeout error, something is likely broken.
 */
const CODE_RUNNER_TIMEOUT_MS = 10000;

interface CodeRunnerContextValue {
  iframeRef: MutableRefObject<HTMLIFrameElement | null> | null;
  isIframeLoaded: MutableRefObject<boolean> | null;
}

const CodeRunnerContext = createContext<CodeRunnerContextValue>({
  iframeRef: null,
  isIframeLoaded: null,
});

export const CodeRunnerProvider: FC<PropsWithChildren<object>> = ({ children }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isIframeLoaded = useRef(false);

  useEffect(() => {
    const handleIframeLoad = () => {
      isIframeLoaded.current = true;
    };

    if (!iframeRef.current) return;

    // iframe is ready to recieve messages when the load event fires
    iframeRef.current.addEventListener('load', handleIframeLoad);
  }, []);

  // import.meta.env.PROD is true for both staging and production environments
  const src = import.meta.env.PROD
    ? 'https://www.withpersona-sandbox.com'
    : // direct development to our development bucket for easier testing
      'https://storage.googleapis.com/persona-frontend-sandbox-development/index.html';

  return (
    <CodeRunnerContext.Provider value={{ iframeRef, isIframeLoaded }}>
      <iframe style={{ display: 'none' }} ref={iframeRef} src={src} sandbox='allow-scripts' />
      {children}
    </CodeRunnerContext.Provider>
  );
};

export type RunCodeFunction = (code: string, args: Record<string, unknown>) => Promise<unknown>;

export const useCodeRunner = () => {
  const { iframeRef, isIframeLoaded } = useContext(CodeRunnerContext);

  const runCode: RunCodeFunction = useCallback(
    (code, args) => {
      return new Promise((resolve, reject) => {
        let retries = 0;
        const postMessage = async () => {
          // check to see if coderunner is loaded, if not wait and retry.
          if (!isIframeLoaded?.current) {
            if (retries < 10) {
              setTimeout(
                () => {
                  postMessage();
                },
                1000 * (retries + 1),
              );
              retries += 1;
            } else {
              reject(new Error('Iframe failed to load error'));
            }
            return;
          }

          const messageId = uuid();

          // setup listener on code runner iframe
          const handleReturnMessage = (event: MessageEvent) => {
            // make sure the message source is coming from our iframe
            if (event.source !== iframeRef?.current?.contentWindow) return;

            // ignore messages from other requests
            if (event.data.messageId !== messageId) return;

            // reject if return message has an error object
            if (event.data.error) {
              reject(event.data.error);
            }

            // resolve promise with return value
            resolve(event.data.returnValue);

            // cleanup listeners and timeout
            window.removeEventListener('message', handleReturnMessage);
            clearTimeout(timeout);
          };

          const timeout = setTimeout(() => {
            reject(new Error('Code runner timeout error'));
            window.removeEventListener('message', handleReturnMessage);
          }, CODE_RUNNER_TIMEOUT_MS);
          window.addEventListener('message', handleReturnMessage);

          const sanitizedArgs = sanitizeArgs(args);

          iframeRef?.current?.contentWindow?.postMessage(
            {
              code,
              args: sanitizedArgs,
              messageId,
            },
            '*', // this has to be a wildcard as sandboxed iframes without allow-same-origin have a null origin
          );
        };
        postMessage();
      }).catch((error) => {
        captureException(error, 'info', {
          error,
          code,
        });
        // let the error bubble up since we're only catching it to log it
        throw error;
      });
    },
    [iframeRef, isIframeLoaded],
  );

  return { runCode };
};

/**
 * Takes a record of arguments and removes any keys that are reserved JavaScript keywords or have dashes.
 * This is necessary because reserved keywords and keys with dashes can not be used to generate JS Function args.
 *
 * Long-term there is a better solution to this problem where we prefix all args, but in order to prefix all usage
 * of the args, we'll have to parse the code into an AST first with a library like acorn.
 */
function sanitizeArgs(args: Record<string, unknown>) {
  return Object.entries(args).reduce(
    (acc, [key, value]) => {
      if (RESERVED_JAVASCRIPT_KEYWORDS.includes(key)) return acc;
      if (key.includes('-')) return acc;

      acc[key] = value;
      return acc;
    },
    {} as Record<string, unknown>,
  );
}
